<script setup lang="ts">
import WrButton from "~/components/global/WrButton.vue";
import WrIcon from "~/components/global/WrIcon.vue";

export interface IndustryCard {
  number: string;
  title: string;
  desc: string;
  svgIcon?: string;
}

const props = withDefaults(
  defineProps<{
    heading: string;
    subcopy: string;
    cards: IndustryCard[];
    ctaText?: string;
    ctaLink?: string | null;
    swapCardColors?: boolean;
    headingMaxWidth?: string;
    subcopyMaxWidth?: string;
  }>(),
  {
    ctaText: "Discover Your Industry Style",
    ctaLink: null,
    swapCardColors: false,
    headingMaxWidth: "max-w-4xl",
    subcopyMaxWidth: "max-w-[750px]",
  }
);

const emit = defineEmits<{
  (e: "cta-clicked"): void;
}>();
</script>

<template>
  <section class="my-16 md:my-20 lg:my-24 relative px-4 sm:px-6 lg:px-8 industry-section-bg">
    <div class="max-w-6xl mx-auto">
      <!-- Section Main Heading (Safe Text Rendering) -->
      <h2
        :class="[headingMaxWidth, 'text-2xl sm:text-3xl pb-1 lg:text-5xl font-semibold leading-tight text-center font-avant-demi tracking-tight mx-auto bg-gradient-to-r from-[#12dbff] to-[#ba55fe] bg-clip-text text-transparent']"
      >
        <span v-html="heading" />
      </h2>

      <!-- Subcopy Description -->
      <p :class="[subcopyMaxWidth, 'text-center mt-3 sm:mt-4 text-sm sm:text-base lg:text-lg opacity-80 mx-auto font-avant font-normal leading-relaxed tracking-wide text-black dark:text-white']">
        {{ subcopy }}
      </p>

      <!-- Responsive Industry Cards Grid -->
      <div class="cards-wrapper relative mt-8 sm:mt-12">
        <div class="cards-grid grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
          <article
            v-for="(item, i) in cards"
            :key="i"
            class="flex items-center p-3 sm:p-4 lg:p-6 relative transition-shadow duration-300"
          >
          <!-- Left Icon Box -->
          <div class="flex items-center justify-center p-2 flex-shrink-0 text-[#613FAA] dark:text-white">
            <slot :name="`icon-${i}`" :index="i">
              <WrIcon
                v-if="item.svgIcon"
                :name="item.svgIcon"
                class="size-8 max-sm:size-6"
              />
              <div
                v-else
                class="size-8 max-sm:size-6 rounded-lg border-2 border-current opacity-40 flex items-center justify-center"
                aria-hidden="true"
              >
                <span class="text-xs font-bold select-none">+</span>
              </div>
            </slot>
          </div>

          <!-- Vertical Separator Divider -->
          <div class="w-px h-10 bg-[#A550FF] opacity-60 mx-3 flex-shrink-0"></div>

          <!-- Right Card Details -->
          <div class="flex flex-col flex-1 min-w-0 justify-center">
            <span
              class="text-2xl sm:text-3xl font-semibold leading-none font-avant-demi mb-1 text-[#613FAA] dark:text-[#FFF] opacity-60"
              :class="{
                '!text-[#A550FF] dark:!text-[#FFF]': swapCardColors
              }"
            >
              {{ item.number }}
            </span>
            <h3
              class="text-sm sm:text-base font-semibold leading-snug font-avant whitespace-normal break-words text-[#613FAA] dark:text-[#FFF]"
              :class="{
                '!text-[#A550FF] dark:!text-[#FFF]': swapCardColors
              }"
            >
              {{ item.title }}
            </h3>
            <p
              class="mt-1 text-xs sm:text-sm leading-relaxed font-avant opacity-90 text-black dark:text-[#A550FF]"
              :class="{
                '!text-[#000000] dark:!text-[#FFF]': swapCardColors
              }"
            >
              {{ item.desc }}
            </p>
          </div>
        </article>
        </div>
      </div>

      <!-- Responsive Action CTA Button -->
      <div class="flex justify-center mt-8 sm:mt-12">
        <WrButton
          :link="ctaLink || undefined"
          @click="!ctaLink ? emit('cta-clicked') : undefined"
          container-class="h-10 lg:h-12 w-full sm:w-auto"
          append-class="text-sm lg:text-base font-normal px-6 sm:px-8 w-full sm:w-auto justify-center"
        >
          {{ ctaText }}
        </WrButton>
      </div>
    </div>
  </section>
</template>

<style scoped>
.cards-wrapper {
  position: relative;
}

.cards-grid {
  position: relative;
}

/* Horizontal divider — full width between rows */
.cards-wrapper::before {
  content: '';
  position: absolute;
  top: 50%;
  left: 0;
  right: 0;
  height: 2px;
  transform: translateY(-50%);
  background: linear-gradient(
    to right,
    transparent 0%,
    rgba(165, 80, 255, 0.3) 10%,
    rgba(165, 80, 255, 0.3) 90%,
    transparent 100%
  );
  pointer-events: none;
  z-index: 1;
}

/* Vertical dividers — sit in the gap between columns */
.cards-grid::before,
.cards-grid::after {
  content: '';
  position: absolute;
  top: 15%;
  bottom: 15%;
  width: 2px;
  pointer-events: none;
  z-index: 1;
  background: linear-gradient(
    to bottom,
    transparent 0%,
    rgba(165, 80, 255, 0.3) 15%,
    rgba(165, 80, 255, 0.3) 85%,
    transparent 100%
  );
}

.cards-grid::before {
  left: 33.333%;
}

.cards-grid::after {
  left: 66.666%;
}

@media (max-width: 1023px) {
  .cards-grid::before,
  .cards-grid::after,
  .cards-wrapper::before {
    display: none;
  }
}
</style>